<template>
  <layout-navigation>
    <section :class="$style.page">
      <header :class="$style.header">
        <store-based-date-time-picker
          :clearable="false"
          @update="updateData"
        />
        <vue-select
          v-if="isNotif"
          v-model="selectedCategory"
          :placeholder="$t('notif.allCategories')"
          :options="categoryOptions"
          :reduce="(option) => option.id"
          label="name"
          :searchable="false"
          @input="filterTableData"
        >
          <template #header>
            <span class="nameSpan">{{ $t('notif.eventCategory') }}</span>
          </template>
        </vue-select>

        <vue-select
          v-if="isNotif"
          v-model="selectedStatus"
          :placeholder="$t('notif.allEvents')"
          :options="statusOptions"
          :reduce="(option) => option.id"
          label="name"
          :searchable="false"
          @input="filterTableData"
        >
          <template #header>
            <span class="nameSpan">{{ $t('notif.eventStatus') }}</span>
          </template>
        </vue-select>

        <div :class="$style.container">
          <label>
            <span :class="$style.visuallyHidden">{{ $t('notif.search') }}</span>
            <input
              v-model="searchString"
              :class="$style.input"
              type="text"
              :placeholder="$t('notif.search')"
              @input="filterTableData"
            >
          </label>
        </div>
        <export />
      </header>
      <group-table
        id="printable"
        :table-data="filterData"
        :empty-result-string="$t('notif.noDataForSelectedPeriod')"
        :columns="columns"
        :is-pending="isPending"
        :is-fetch="isFetch"
        :is-infinit-scroll="isNotif"
        :selected-data="selectedData"
        @changeSort="changeSort"
        @filterClick="filterClick"
        @update="fetch"
      />
    </section>
  </layout-navigation>
</template>

<script>
  import dayjs from 'dayjs';
  import VueSelect from 'vue-select';
  import LayoutNavigation from '@/layouts/navigation.vue';
  import StoreBasedDateTimePicker from '@/components/common/filter/store-based-date-time-picker.vue';
  import { getNotificationsHistory } from '@/api/methods/notifications';
  import { getSysLogs } from '@/api/methods/settings';
  import GroupTable from '@/components/common/table/tableWithGrouping.vue';
  import OpenIndicator from '@/components/common/header/vue-select-custom/open-indicator.vue';
  import Export from '@/components/pages/computers/log/screenshots/components-header/export.vue';
  import { getDateUTCFromBuildReport } from '@/helpers/format-date';
  import { getDataField } from '@/helpers/fields';
  import { errorToast } from '@/utils/open-toast';
  import { COLUMNS_NOTIF, COLUMNS_SYS_LOGS } from './const/const';

  VueSelect.props.components.default = () => ({ OpenIndicator });

  export default {
    components: {
      LayoutNavigation,
      StoreBasedDateTimePicker,
      GroupTable,
      VueSelect,
      Export,
    },
    data() {
      return {
        updateNeeded: false,
        searchString: '',
        selectedCategory: undefined,
        selectedStatus: undefined,
        tableData: [],
        filterData: [],
        selectedData: {},
        page: 0,
        limit: 50,
        isFetch: false,
        isPending: false,
        reasonApplied: false,
        statusOptions: [
          {
            id: 1,
            name: this.$t('notif.statusOptions.confirmedOnly'),
          },
          {
            id: 0,
            name: this.$t('notif.statusOptions.unconfirmed'),
          },
        ],
        categoryOptions: [
          {
            id: 1,
            name: this.$t('notif.categoryOptions.textInput'),
          },
          {
            id: 2,
            name: this.$t('notif.categoryOptions.programs'),
          },
          {
            id: 3,
            name: this.$t('notif.categoryOptions.fileSystem'),
          },
          {
            id: 4,
            name: this.$t('notif.categoryOptions.clipboard'),
          },
          {
            id: 6,
            name: this.$t('notif.categoryOptions.installUninstallPrograms'),
          },
          {
            id: 7,
            name: this.$t('notif.categoryOptions.visitedSites'),
          },
          {
            id: 10,
            name: this.$t('notif.categoryOptions.shadowCopy'),
          },
          {
            id: 11,
            name: this.$t('notif.categoryOptions.deviceConnections'),
          },
          {
            id: 12,
            name: this.$t('notif.categoryOptions.activeAlert'),
          },
          {
            id: 13,
            name: this.$t('notif.categoryOptions.email'),
          },
          {
            id: 17,
            name: this.$t('notif.categoryOptions.networkFiles'),
          },
          {
            id: 18,
            name: this.$t('notif.categoryOptions.messengers'),
          },
          {
            id: 20,
            name: this.$t('notif.categoryOptions.socialNetworks'),
          },
          {
            id: 21,
            name: this.$t('notif.categoryOptions.webEmail'),
          },
          {
            id: 22,
            name: this.$t('notif.categoryOptions.exportedFiles'),
          },
          {
            id: 23,
            name: this.$t('notif.categoryOptions.printer'),
          },
          {
            id: 24,
            name: this.$t('notif.categoryOptions.messengersFiles'),
          },
          {
            id: 26,
            name: this.$t('notif.categoryOptions.hardwareChanges'),
          },
        ],
      };
    },
    computed: {
      isNotif() {
        return this.$route.name === 'notifications';
      },
      columns() {
        return this.isNotif ? COLUMNS_NOTIF : COLUMNS_SYS_LOGS;
      },
      dateRange() {
        return getDateUTCFromBuildReport(this.$route.query.dateFrom, this.$route.query.dateTo);
      },
      userId() {
        return this.$store.getters['user/userId'];
      },
    },
    watch: {
      async updateNeeded(value) {
        if (value) {
          await this.fetch();

          this.updateNeeded = false;
        }
      },
      filterData(newVal, oldValue) {
        if (newVal.length > oldValue.length) {
          const { name, reasonId } = this.$route.query;
          if (name) {
            const { filter, field } = this.columns[0];

            const matchedNames = filter.filter((item) => item.includes(name));

            if (matchedNames.length) {
              this.$set(this.selectedData, field, matchedNames.slice());
              this.selectedData = {
                ...this.selectedData,
                [field]: [...matchedNames],
              };
            }
            if (reasonId && !this.reasonApplied) {
              this.reasonApplied = true;
              const { field: reasonString } = this.columns[2];
              this.$set(this.selectedData, reasonString, [this.$t(`db.${reasonString}.${reasonId}`)]);
            }
          }
        }
      },
    },
    async created() {
      this.whenUpdated();
    },
    methods: {
      whenUpdated() {
        this.$store.commit('navigation/setNewNotifications', false);

        const { dateFrom, dateTo } = this.$route.query;
        if (dateFrom && dateTo) {
          this.$store.commit('filters/updateDateRange', [dateFrom, dateTo]);
        }

        const queryMutations = {};

        if (!dateFrom) {
          queryMutations.dateFrom = dayjs().startOf('day').format('YYYY-MM-DD HH:mm:ss');
        }

        if (!dateTo) {
          queryMutations.dateTo = dayjs().endOf('day').format('YYYY-MM-DD HH:mm:ss');
        }

        if (Object.keys(queryMutations).length) {
          this.$router.replace({
            query: {
              ...this.$route.query,
              ...queryMutations,
            },
          });
        }

        this.updateNeeded = true;
      },
      filterClick(column) {
        const value = column.showFilter;

        column.showFilter = !value;

        if (column.showFilter) {
          window.addEventListener('click', (e) => {
            if (
              !document.getElementById(`columnFilter${column.field}`).contains(e.target) &&
              !document.getElementById(`filterIcon${column.field}`).contains(e.target)
            ) {
              column.showFilter = false;
              window.removeEventListener('click', e);
            }
          });
        }
      },
      changeSort(column) {
        const { sort } = column;
        this.columns.forEach((col) => {
          col.sort = 0;
        });

        if (sort === 1) {
          column.sort = 2;
        } else column.sort = 1;
      },
      filterTableData() {
        let result = this.tableData;
        if (this.selectedCategory) {
          result = result.filter((item) => item.categoryId === this.selectedCategory);
        }
        if (this.selectedStatus === 1) {
          result = result.filter((item) => item.checkUser !== null);
        }
        if (this.selectedStatus === 0) {
          result = result.filter((item) => item.checkUser === null);
        }

        if (this.searchString?.length) {
          result = result.filter((item) =>
            item?.descriptionOutput.indexOf(this.searchString) !== -1 || item?.keywords
              ? item?.keywords?.indexOf(this.searchString) !== -1
              : false,
          );
        }

        this.filterData = result;
        this.assembleColumnFilter();
      },
      assembleColumnFilter() {
        this.columns.forEach((column) => {
          const filter = [];

          this.filterData.forEach((item) => {
            if (item[column.field] && filter.indexOf(getDataField(item, column.field)) === -1) {
              filter.push(getDataField(item, column.field));
            }
          });
          column.filter = filter;
        });
      },
      clearData() {
        this.tableData = [];
        this.filterData = [];
        this.page = 0;
        this.isFetch = false;
        this.isPending = false;
      },
      async fetch() {
        if (this.isPending || this.isFetch) return;
        this.isPending = true;

        if (this.isNotif) {
          try {
            const result = await getNotificationsHistory(
              this.userId,
              this.dateRange.dateFrom,
              this.dateRange.dateTo,
              this.page,
              this.limit,
            );
            if (result !== 'error') {
              result.forEach((item) => {
                if (!item.descriptionString) {
                  if (item.description) item.descriptionOutput = item.description;
                  else item.descriptionOutput = '';
                } else item.descriptionOutput = item.descriptionString;
              });
              this.tableData = [...this.tableData, ...result];
              this.filterTableData();
              this.assembleColumnFilter();
              this.page++;
              if (result.length < this.limit) this.isFetch = true;
            } else {
              throw new Error();
            }
          } catch (err) {
            errorToast();
          } finally {
            this.isPending = false;
          }
        } else {
          try {
            const result = await getSysLogs(0, this.dateRange.dateFrom, this.dateRange.dateTo);

            if (result !== 'error') {
              result.forEach((item) => {
                if (item?.comment) item.descriptionOutput = item.comment;
                else item.descriptionOutput = '';
              });
              this.tableData = result.filter(({ eventTypeId }) => ![6, 7].includes(eventTypeId));
              this.filterTableData();
              this.assembleColumnFilter();
            } else throw new Error();
          } catch (err) {
            errorToast();
          } finally {
            this.isPending = false;
          }
        }
      },
      async updateData() {
        this.clearData();
        await this.fetch();
      },
    },
  };
</script>

<style lang="scss" module>
  ::-webkit-scrollbar {
    width: 4px;
  }

  ::-webkit-scrollbar-track {
    background: white;
  }

  ::-webkit-scrollbar-thumb {
    background: $branchColor;
    border-radius: 2px;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: darkgray;
  }

  .page {
    background: $white;
    width: 100%;
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: auto;
  }

  .header {
    box-shadow: $shadow;
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 20px;
    padding: 20px 30px;
    justify-content: space-between;
  }

  .control {
    display: flex;
    align-items: center;
  }

  .visuallyHidden {
    position: absolute;
    clip: rect(0 0 0 0);
    width: 1px;
    height: 1px;
    margin: -1px;
  }

  .input {
    border: 1px solid $borderColor;
    color: $black;
    font-size: 14px;
    line-height: 20px;
    padding: 6px 12px;
    height: 50px;
    width: 100%;

    &::placeholder {
      color: dakren($dark-gray, 3%);
    }
  }

  .container {
    width: 25%;
  }
</style>
<style lang="scss">
  .v-select {
    width: 20%;

    & * {
      font-style: normal;
      font-size: 14px;
    }

    .vs__dropdown-toggle {
      height: 54px;
      border: 1px solid #dfdfdf;
      border-radius: 0px;
    }

    &.vs--single.vs--searching span.vs__selected {
      display: none !important;
    }

    .vs__selected-options {
      padding: 13px 0 0 9px;
      min-width: 0;

      .vs__selected {
        display: block;
        height: 30px;
        width: 244px;
        line-height: 30px;
        font-family: inherit;
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
        color: $black;
      }

      .vs__search {
        height: 30px;
        width: 244px;
        line-height: 30px;
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
      }
    }

    .nameSpan {
      position: absolute;
      top: -2px;
      left: 16px;
      font-style: normal;
      font-weight: 300;
      font-size: 12px;
      line-height: 30px;
      color: #7e7e7e;
    }

    ul {
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: auto;
    }

    ul > li {
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;
    }
  }
</style>
